import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet';

// Pages
import Home from "./pages/Home";
import TodoList from "./pages/TodoList";
import MarksCalculator from "./pages/MarksCalculator";
import BudgetManager from "./pages/BudgetManager";
import CountdownTimer from "./pages/CountdownTimer";

// Clean, modern styles for students with mobile optimization
const styles = {
  app: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #a5b2ebff 0%, #d7deefff 100%)',
    backgroundAttachment: 'fixed',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: '#1e293b'
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 12px',
    textAlign: 'center',
    // Mobile optimization
    '@media (min-width: 768px)': {
      padding: '24px 20px'
    }
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '12px',
    letterSpacing: '-0.025em',
    // Desktop size
    '@media (min-width: 768px)': {
      fontSize: '38px',
      marginBottom: '24px'
    }
  },
  nav: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    // Desktop gap
    '@media (min-width: 768px)': {
      gap: '12px'
    }
  },
  navButton: {
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '20px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: '90px',
    justifyContent: 'center',
    transform: 'translateY(0)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    textDecoration: 'none',
    // Desktop styles
    '@media (min-width: 768px)': {
      padding: '12px 24px',
      fontSize: '14px',
      borderRadius: '25px',
      gap: '8px',
      minWidth: '140px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    }
  },
  navButtonActive: {
    background: 'linear-gradient(135deg, #a5b2ebff 0%, #d7deefff 100%)',
    color: 'white',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    // Desktop hover effect
    '@media (min-width: 768px)': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
    }
  },
  navButtonInactive: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#64748b',
    border: '1px solid rgba(255, 255, 255, 0.5)'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 12px',
    // Desktop padding
    '@media (min-width: 768px)': {
      padding: '32px 20px'
    }
  }
};

// Helper function to apply responsive styles
const applyResponsiveStyles = (baseStyles) => {
  const mediaQuery = window.matchMedia('(min-width: 768px)');
  const isDesktop = mediaQuery.matches;
  
  const responsiveStyles = { ...baseStyles };
  
  if (isDesktop && baseStyles['@media (min-width: 768px)']) {
    Object.assign(responsiveStyles, baseStyles['@media (min-width: 768px)']);
  }
  
  // Remove media query properties from the final styles
  delete responsiveStyles['@media (min-width: 768px)'];
  
  return responsiveStyles;
};

// Navigation component that knows about current route
function Navigation({ hoveredButton, setHoveredButton }) {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const tools = [
    { id: '/', name: 'Home', icon: '🏠', path: '/', shortName: 'Home' },
    { id: '/todo', name: 'Calendar To-do List', icon: '📝', path: '/todo', shortName: 'To-do' },
    { id: '/calculator', name: 'Marks Calculator', icon: '📊', path: '/calculator', shortName: 'Marks' },
    { id: '/budget', name: 'Budget Manager', icon: '💰', path: '/budget', shortName: 'Budget' },
    { id: '/timer', name: 'Countdown Timer', icon: '⏰', path: '/timer', shortName: 'Timer' }
  ];

  const getButtonStyle = (path) => {
    const baseStyle = applyResponsiveStyles(styles.navButton);
    const activeStyle = location.pathname === path ? styles.navButtonActive : styles.navButtonInactive;
    const hoverStyle = hoveredButton === path && location.pathname !== path ? {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: isMobile ? 'translateY(-1px)' : 'translateY(-2px)',
      boxShadow: isMobile ? '0 4px 15px rgba(0, 0, 0, 0.15)' : '0 8px 25px rgba(0, 0, 0, 0.15)'
    } : {};
    
    return {
      ...baseStyle,
      ...activeStyle,
      ...hoverStyle
    };
  };

  return (
    <nav style={applyResponsiveStyles(styles.nav)}>
      {tools.map((tool) => (
        <Link
          key={tool.id}
          to={tool.path}
          style={getButtonStyle(tool.path)}
          onMouseEnter={() => setHoveredButton(tool.path)}
          onMouseLeave={() => setHoveredButton(null)}
          title={tool.name} // Show full name on hover for mobile
        >
          <span>{tool.icon}</span>
          <span>{isMobile ? tool.shortName : tool.name}</span>
        </Link>
      ))}
    </nav>
  );
}

// Main App Component
function App() {
  const [hoveredButton, setHoveredButton] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Todo List State
  const [tasks, setTasks] = useState([]);
  const [activeReminders, setActiveReminders] = useState([]);

  // Timer State
  const [timers, setTimers] = useState([]);

  // Donation box visibility state
  const [showDonationBox, setShowDonationBox] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <HelmetProvider>
    <Router>
      <div style={styles.app}>
        <div style={styles.header}>
          <div style={applyResponsiveStyles(styles.headerContent)}>
            <h1 style={applyResponsiveStyles(styles.title)}>🎓 Student Tools Hub</h1>
            <Navigation 
              hoveredButton={hoveredButton}
              setHoveredButton={setHoveredButton}
            />

            {/* Floating Donation Box - Adjusted for mobile */}
            {showDonationBox && (
              <div style={{
                position: "fixed",
                bottom: isMobile ? "12px" : "20px",
                right: isMobile ? "12px" : "20px",
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                padding: isMobile ? "12px" : "16px",
                width: isMobile ? "200px" : "260px",
                zIndex: 100,
                textAlign: "center"
              }}>
                {/* Close button */}
                <button
                  onClick={() => setShowDonationBox(false)}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#f1f5f9",
                    border: "none",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    color: "#64748b",
                    transition: "all 0.2s",
                    zIndex: 101
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e2e8f0";
                    e.target.style.color = "#475569";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f1f5f9";
                    e.target.style.color = "#64748b";
                  }}
                  title="Close"
                >
                  ✕
                </button>

                <div style={{
                  position: "absolute",
                  bottom: "-8px",
                  right: "25px",
                  width: "0",
                  height: "0",
                  borderLeft: "12px solid transparent",
                  borderRight: "12px solid transparent", 
                  borderTop: "12px solid white",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                }} />
                
                <div style={{
                  position: "absolute",
                  top: "2px",
                  left: "2px",
                  right: "2px", 
                  bottom: "2px",
                  borderRadius: "16px",
                  background: "linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.2) 100%)",
                  pointerEvents: "none",
                  zIndex: -1
                }} />

                <h3 style={{ 
                  fontSize: isMobile ? "16px" : "20px", 
                  fontWeight: "600", 
                  marginBottom: "8px" 
                }}>💖 Support My Work</h3>
                <p style={{ 
                  fontSize: isMobile ? "12px" : "14px", 
                  color: "#64748b", 
                  marginBottom: "12px" 
                }}>
                  If you find this app useful, consider supporting me!
                </p>
                <a
                  href="https://www.paypal.me/studenttoolshub666"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: isMobile ? "8px 14px" : "10px 18px",
                    backgroundColor: "#749fe5ff",
                    color: "white",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontWeight: "600",
                    fontSize: isMobile ? "12px" : "14px"
                  }}
                >
                  ☕ Buy Me a Coffee
                </a>
              </div>
            )}
          </div>
        </div>

        <div style={applyResponsiveStyles(styles.container)}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/todo" element={
              <TodoList 
                tasks={tasks}
                setTasks={setTasks}
                activeReminders={activeReminders}
                setActiveReminders={setActiveReminders}
              />
            }/>
            <Route path="/calculator" element={<MarksCalculator />} />
            <Route path="/budget" element={<BudgetManager />} />
            <Route path="/timer" element={
              <CountdownTimer 
                timers={timers}
                setTimers={setTimers}
              />
            }/>
          </Routes>
        </div>
      </div>
    </Router>
    </HelmetProvider>
  );
}

export default App;